import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Switch } from "components/ui/switch";
import { Spinner } from "components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { ScrollArea } from "components/ui/scroll-area";
import { Separator } from "components/ui/separator";
import {
  Bookmark,
  Checkmark,
  Close,
  Link,
  User,
  Plus,
  ArrowRight,
  Building,
  Document,
} from "assets/icons";
import { CalendarIcon, X } from "@phosphor-icons/react";
import { API_URL } from "constants/index";
import { UserSelection } from "../userSelection/userSelection";
import { toast } from "react-toastify";
import ResourceSelection from "../resourceSelection/resourceSelection";
import LabelSelection from "../labelSelection/labelSelection";
import { useSession, useUser } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useTasks } from "shared/contexts/tasks.context";
import { TaskLabel, TaskPhase, TaskPriority } from "shared/types/task";
import { Resource, ResourceType } from "shared/types/resource";
import { Id } from "components/kanbanBoard/types";
import { ClerkUser } from "shared/types/user";
import { getIconForLabel } from "shared/utils/get-icons";
import { taskPhaseColumns, getPhaseForColumn } from "screens/tasks/taskPhaseConfig";

export type AvailableLabel = {
  name: TaskLabel;
  icon: JSX.Element | null;
};

export type AvailableLabels = AvailableLabel[];

interface AddTaskModalProps {
  phase: TaskPhase;
  onClose: () => void;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  fetchTasks?: () => void;
}

const AddTaskModal: React.FC<AddTaskModalProps> = ({
  phase,
  onClose,
  isOpen = false,
  onOpenChange,
}) => {
  const { refreshTasks } = useTasks();
  const [title, setTitle] = useState("");
  const [selectedPriority, setSelectedPriority] = useState(0);
  const [description, setDescription] = useState("");
  const [taskLabels, setTaskLabels] = useState<AvailableLabels>([]);
  const [taskDate, setTaskDate] = useState("");
  const [selectedResources, setSelectedResources] = useState<Resource[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState<ClerkUser | null>(null);
  const [selectedAssignedBy, setSelectedAssignedBy] = useState<ClerkUser | null>(null);
  const session = useSession();
  const { user } = useUser();
  const [files, setFiles] = useState<{ file: File; sharePointFolderId?: string }[]>([]);
  const [totalFileSize, setTotalFileSize] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveToCalendar, setSaveToCalendar] = useState(false);
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const [taskPhase, setPhase] = useState<TaskPhase>(phase);

  const resetState = () => {
    setTitle("");
    setPhase(TaskPhase.INBOX);
    setSelectedPriority(0);
    setDescription("");
    setTaskLabels([]);
    setTaskDate("");
    setSelectedResources([]);
    setSelectedAssignee(null);
    setSelectedAssignedBy(null);
    setFiles([]);
    setTotalFileSize(0);
    setSaveToCalendar(false);
  };

  useEffect(() => {
    if (isOpen) {
      setPhase(phase);
    } else {
      resetState();
    }
  }, [isOpen, phase]);

  const availableLabels: AvailableLabels = Object.values(TaskLabel)
    .map((label) => ({
      icon: getIconForLabel(label),
      name: label,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const today = new Date().toISOString().split("T")[0];

  const addResourceConnection = (resource: Resource) => {
    setSelectedResources((prevResources) => [...prevResources, resource]);
  };

  const removeResourceConnection = (resourceId: Id) => {
    setSelectedResources((prevResources) =>
      prevResources.filter((resource) => resource.id !== resourceId),
    );
  };

  const handleRemoveFile = (indexToRemove: number, size: number) => {
    setFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove),
    );
    setTotalFileSize(
      (prevTotalFileSize) => prevTotalFileSize - size / (1024 * 1024),
    );
  };

  const saveTask = async () => {
    if (!title.trim()) {
      toast.error("De taak moet een titel hebben!");
      return;
    }

    setIsSaving(true);
    const formData = new FormData();

    formData.append("title", title);
    formData.append("description", description);
    formData.append("priority", selectedPriority.toString());
    formData.append(
      "deadline",
      taskDate ? new Date(taskDate).toISOString() : "",
    );
    formData.append(
      "resources",
      JSON.stringify(selectedResources.map((resource) => resource.id)),
    );
    formData.append(
      "labels",
      JSON.stringify(taskLabels.map((label) => ({ name: label.name }))),
    );
    formData.append(
      "phase",
      phase ? phase : taskPhase ? taskPhase : TaskPhase.INBOX,
    );

    if (selectedAssignee) {
      formData.append(
        "assignee",
        JSON.stringify({
          id: selectedAssignee.id,
          email: selectedAssignee.email_addresses[0].email_address,
          firstName: selectedAssignee.first_name,
        }),
      );
    } else {
      formData.append(
        "assignee",
        JSON.stringify({
          id: user?.id,
          email: user?.emailAddresses[0].emailAddress,
          firstName: user?.firstName,
        }) || JSON.stringify({ id: "" }),
      );
    }

    if (selectedAssignedBy) {
      formData.append(
        "assignedBy",
        JSON.stringify({
          id: selectedAssignedBy.id,
          email: selectedAssignedBy.email_addresses[0].email_address,
          firstName: selectedAssignedBy.first_name,
        }),
      );
    } else {
      formData.append(
        "assignedBy",
        JSON.stringify({
          id: user?.id,
          email: user?.emailAddresses[0].emailAddress,
          firstName: user?.firstName,
        }) || JSON.stringify({ id: "" }),
      );
    }

    if (saveToCalendar && selectedAssignee) {
      formData.append("bookInCalendar", "true");
      const sharepointUser = {
        sharepointUserId: selectedAssignee.public_metadata.sharepointUserId,
        sharepointCalendarId:
          selectedAssignee.public_metadata.sharepointCalendarId,
      };
      formData.append("sharepointUser", JSON.stringify(sharepointUser));
    }

    // Adding files
    files.forEach((fileObj, index) => {
      formData.append("file" + index, fileObj.file);
      if (fileObj.sharePointFolderId) {
        formData.append("folderId" + index, fileObj.sharePointFolderId);
      }
    });

    console.log("🚀 ~ saveTask ~ formData:", formData)
    console.log("🚀 ~ saveTask ~ session.session:", session.session)
    console.log("🚀 ~ saveTask ~ API_URL:", API_URL)
    console.log("🚀 ~ saveTask ~ `${API_URL}/tasks/create`:", `${API_URL}/tasks/create`)
    console.log("🚀 ~ saveTask ~ session.session:", session.session)
    console.log("🚀 ~ saveTask ~ API_URL:", API_URL)

    try {
      const response = await fetchWithAuth(
        `${API_URL}/tasks/create`,
        {
          method: "POST",
          body: formData,
        },
        session.session,
      );

      await response.json();
      toast.success("Taak opgeslagen!");
      await refreshTasks();
      onClose();
    } catch (error) {
      console.error("There was an error saving the task:", error);
      toast.error("Er is een fout opgetreden bij het opslaan van de taak");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {isSaving && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="flex items-center gap-3 rounded-lg bg-white p-6 shadow-xl">
            <Spinner className="h-6 w-6" />
            <span className="text-lg font-semibold text-brick-primary">Taak aan het opslaan...</span>
          </div>
        </div>
      )}
      
      <Dialog open={isOpen && !isSaving} onOpenChange={(open) => {
        onOpenChange(open);
        if (!open) onClose();
      }} modal={true}>
        <DialogContent 
          className="max-w-4xl max-h-[90vh] p-0"
          onOpenAutoFocus={(e) => {
            // Prevent auto focus to avoid conflict with HeroUI dropdown focus trap
            e.preventDefault();
          }}
          onCloseAutoFocus={(e) => {
            // Prevent auto focus return to avoid conflict
            e.preventDefault();
          }}
        >
          <DialogHeader className="sticky top-0 z-10 bg-white border-b px-6 py-4">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-semibold">Nieuwe taak toevoegen</DialogTitle>
              <div className="flex items-center gap-2">
                <Button
                  onClick={saveTask}
                  className="bg-moss-primary hover:bg-moss-primary/90"
                  disabled={isSaving}
                >
                  <Checkmark className="mr-2 h-4 w-4" />
                  Bewaren
                </Button>
                <Button
                  onClick={onClose}
                  variant="ghost"
                  size="icon"
                >
                  <Close className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="max-h-[calc(90vh-120px)]">
            <div className="space-y-6 px-6 py-4">
              {/* Title and Description */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-sm font-medium">
                    Titel <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Voer een titel in..."
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-sm font-medium">
                    Beschrijving
                  </Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Voeg een beschrijving toe..."
                    className="min-h-[100px] resize-y"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phase" className="text-sm font-medium">
                    In welke fase komt deze taak?
                  </Label>
                  <Select value={taskPhase} onValueChange={(value) => setPhase(value as TaskPhase)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Selecteer een fase" />
                    </SelectTrigger>
                    <SelectContent>
                      {taskPhaseColumns.map((column) => (
                        <SelectItem key={column.id} value={getPhaseForColumn(column.id)}>
                          {column.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Metadata Section */}
              <div className="rounded-lg bg-neutral-50 p-4 space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Date Picker */}
                  <div className="flex items-center gap-2">
                    <CalendarIcon size={18} className="text-neutral-600" weight="duotone" />
                    <input
                      ref={dateInputRef}
                      type="date"
                      value={taskDate}
                      min={today}
                      onChange={(e) => setTaskDate(e.target.value)}
                      className="rounded-md border border-neutral-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-moss-primary"
                    />
                  </div>

                  <Separator orientation="vertical" className="h-6" />

                  {/* Label Selection */}
                  <LabelSelection
                    taskLabels={taskLabels}
                    setTaskLabels={setTaskLabels}
                    availableLabels={availableLabels}
                  />

                  <Separator orientation="vertical" className="h-6" />

                  {/* User Assignment */}
                  <div className="flex items-center gap-2">
                    <UserSelection
                      defaultUser={user}
                      onUserSelect={setSelectedAssignedBy}
                    />
                    <ArrowRight className="h-4 w-4 text-neutral-400" />
                    <UserSelection
                      defaultUser={user}
                      onUserSelect={setSelectedAssignee}
                    />
                  </div>

                  <Separator orientation="vertical" className="h-6" />

                  {/* Resource Selection */}
                  <ResourceSelection
                    addResourceConnection={addResourceConnection}
                    removeResourceConnection={removeResourceConnection}
                  />
                </div>

                {/* Priority Buttons */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-neutral-600">Prioriteit:</span>
                  <Button
                    type="button"
                    size="sm"
                    variant={selectedPriority === TaskPriority.LOW ? "default" : "outline"}
                    onClick={() => setSelectedPriority(TaskPriority.LOW)}
                    className={`rounded-full ${
                      selectedPriority === TaskPriority.LOW
                        ? "bg-status-good hover:bg-status-good/90 text-white border-status-good"
                        : "border-status-good text-status-good hover:bg-status-good/10"
                    }`}
                  >
                    Laag
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={selectedPriority === TaskPriority.MEDIUM ? "default" : "outline"}
                    onClick={() => setSelectedPriority(TaskPriority.MEDIUM)}
                    className={`rounded-full ${
                      selectedPriority === TaskPriority.MEDIUM
                        ? "bg-status-medium hover:bg-status-medium/90 text-white border-status-medium"
                        : "border-status-medium text-status-medium hover:bg-status-medium/10"
                    }`}
                  >
                    Gemiddeld
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={selectedPriority === TaskPriority.HIGH ? "default" : "outline"}
                    onClick={() => setSelectedPriority(TaskPriority.HIGH)}
                    className={`rounded-full ${
                      selectedPriority === TaskPriority.HIGH
                        ? "bg-status-bad hover:bg-status-bad/90 text-white border-status-bad"
                        : "border-status-bad text-status-bad hover:bg-status-bad/10"
                    }`}
                  >
                    Hoog
                  </Button>
                </div>
              </div>

              {/* Calendar Integration Toggle */}
              {selectedAssignee &&
                selectedAssignee.public_metadata.sharepointUserId &&
                selectedAssignee.public_metadata.sharepointCalendarId &&
                taskDate && (
                  <div className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white p-4">
                    <Label htmlFor="calendar-toggle" className="text-sm font-medium cursor-pointer">
                      Voeg afspraak toe in outlook kalender van{" "}
                      {selectedAssignee.first_name + " " + selectedAssignee.last_name}
                    </Label>
                    <Switch
                      id="calendar-toggle"
                      checked={saveToCalendar}
                      onCheckedChange={setSaveToCalendar}
                    />
                  </div>
                )}

              <Separator />

              {/* File Upload Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Bookmark className="h-5 w-5 text-neutral-600" />
                  <h3 className="text-sm font-semibold">Bijlagen uploaden</h3>
                </div>

                <div
                  className="rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50/50 p-6 cursor-pointer hover:border-moss-primary/50 hover:bg-moss-primary/5 transition-colors"
                  onClick={() => document.getElementById("fileInput")?.click()}
                >
                  {files.length > 0 ? (
                    <div className="space-y-3">
                      {files.map((fileObj, index) => (
                        <div
                          key={fileObj.file.name}
                          className="flex items-center gap-3 rounded-md bg-white border border-neutral-200 p-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Document className="h-5 w-5 text-neutral-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {fileObj.file.name}
                            </p>
                            <p className="text-xs text-neutral-500">
                              {(fileObj.file.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                          <Select
                            value={fileObj.sharePointFolderId || ""}
                            onValueChange={(value) => {
                              setFiles((prevFiles) =>
                                prevFiles.map((f, idx) =>
                                  idx === index
                                    ? { ...f, sharePointFolderId: value }
                                    : f,
                                ),
                              );
                            }}
                          >
                            <SelectTrigger className="w-[200px]">
                              <SelectValue placeholder="Standaard taak folder" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">Standaard taak folder</SelectItem>
                              {selectedResources.map((resource) => (
                                <SelectItem
                                  key={resource.id}
                                  value={resource.sharePointFolderId || ""}
                                >
                                  {resource.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile(index, fileObj.file.size);
                            }}
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-sm text-neutral-500">
                        Klik hier of sleep bestanden om bij te voegen
                      </p>
                    </div>
                  )}
                  <input
                    type="file"
                    id="fileInput"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => {
                      if (e.target.files) {
                        const newFiles = Array.from(e.target.files).map(
                          (file) => ({
                            file,
                            sharePointFolderId: "",
                            sharePointFolderUrl: "",
                          }),
                        );
                        setFiles((prevFiles) => [...prevFiles, ...newFiles]);

                        let total = totalFileSize;
                        for (let i = 0; i < newFiles.length; i++) {
                          total += newFiles[i].file.size;
                        }
                        setTotalFileSize(total / (1024 * 1024));
                      }
                    }}
                  />
                </div>

                <Button
                  type="button"
                  onClick={() => document.getElementById("fileInput")?.click()}
                  variant="outline"
                  className="w-full"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Selecteer bestand
                </Button>
              </div>

              <Separator />

              {/* Connected Resources Section */}
              {selectedResources && selectedResources.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Link className="h-5 w-5 text-neutral-600" />
                    <h3 className="text-sm font-semibold">Koppelingen</h3>
                  </div>

                  <div className="space-y-2">
                    {selectedResources.map((resource) => (
                      <div
                        key={resource.id}
                        className="relative flex items-center gap-4 rounded-lg border border-neutral-200 bg-white p-4 hover:border-moss-primary/50 hover:bg-moss-primary/5 transition-colors"
                      >
                        <div className="flex-shrink-0">
                          {resource.type === "building" ? (
                            <Building className="h-6 w-6 text-moss-primary" />
                          ) : resource.type === ResourceType.CONTACT ? (
                            <User className="h-6 w-6 text-moss-primary" />
                          ) : null}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm truncate">{resource.name}</h4>
                          {resource.metadata1 && (
                            <p className="text-xs text-neutral-500 truncate">{resource.metadata1}</p>
                          )}
                          {resource.metadata2 && (
                            <div className="mt-1 flex gap-1 flex-wrap">
                              {resource.metadata2.split(",").map((label, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {label}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeResourceConnection(resource.id)}
                          className="absolute right-2 top-2 text-xs"
                        >
                          <X size={14} className="mr-1" />
                          Verbreek
                        </Button>

                        <a
                          href={`/${resource.type}/${resource.resourceId}`}
                          className="flex-shrink-0"
                        >
                          <Button type="button" size="icon" variant="ghost">
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddTaskModal;
